import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../styles';
import * as subscriptionService from '../services/subscriptionService';
import config from '../config';
import storeLinks from '@budgit/store-links';
import planLimits from '@budgit/plan-limits';

// The free-tier bank limit the backend enforces (#2468). Never write the number.
const FREE_BANKS = planLimits.FREE_INSTITUTION_LIMIT;

const APP_STORE_URL = storeLinks.buildAppStoreListingUrl();

const PricingCard = styled(Card)(({ featured }) => ({
  height: '100%',
  borderRadius: '20px',
  backgroundColor: colors.white,
  boxShadow: 'none',
  border: featured ? `2px solid ${colors.primary}` : `1px solid ${colors.primary}20`,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
}));

const FeatureIcon = styled(Box)(({ included }) => ({
  color: included ? colors.primary : colors.status.error,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const comparisonRows = [
  { name: 'Plaid bank connectivity', free: `Up to ${FREE_BANKS} banks`, premium: 'Unlimited accounts' },
  { name: 'AI onboarding (first budget, initial categorization, income detection)', free: 'Yes', premium: 'Yes' },
  { name: 'Ongoing AI auto-categorization', free: 'No (manual)', premium: 'Yes' },
  { name: 'Low-confidence AI prompts', free: 'No', premium: 'Yes' },
  { name: 'Active budgets per period', free: '1', premium: 'Unlimited' },
  { name: 'Receipt scanner', free: 'Yes', premium: 'Yes' },
  { name: 'Reimbursement tracker', free: 'Yes', premium: 'Yes' },
  { name: 'Manual transaction entry', free: 'Yes', premium: 'Yes' },
  { name: 'Community feed & contest participation', free: 'Yes (limited points)', premium: 'Yes (competitive points)' },
  { name: 'Family sharing (spouse, children)', free: 'No', premium: 'Yes' },
];

const faqs = [
  {
    q: 'What’s included in Free?',
    a: `Bank sync for up to ${FREE_BANKS} banks, AI that builds your first budget during onboarding, receipt scanning, reimbursements, one active budget per period, and community access with limited contest points. After onboarding, categorization is manual unless you upgrade.`,
  },
  {
    q: 'Why pay for Premium?',
    a: 'Ongoing AI that keeps learning your patterns, unlimited accounts and budgets, low-confidence review prompts, competitive contests, and family sharing. Switching apps later means restarting the learning loop. Your Budgit model already knows you.',
  },
  {
    q: 'What is the Founding Member IAP?',
    a: `On iOS, Premium is offered as the Founding Member in-app purchase at $${config.PRICING_DISPLAY.APPLE_FOUNDER_MONTHLY}/mo. Same Premium entitlements, purchased and managed through Apple. Listing surfaces stay price-silent.`,
  },
  {
    q: 'Is the web founding special still available?',
    a: `Yes. The founding price of $${config.PRICING_DISPLAY.PREMIUM_FOUNDER_MONTHLY}/mo is available to anyone who joins before ${config.FOUNDING_MEMBER_DEADLINE}.`,
  },
  {
    q: 'Is there a free Premium trial?',
    a: 'Yes. 90 free days of Premium (offer). Cancel anytime. Listing and this page may mention the offer. The first screen does not claim you are enrolled until you opt in or your account is actually trialing.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. iOS via App Store subscriptions. Web via the billing portal. Access continues through the paid or trial period when canceled.',
  },
  {
    q: 'Do you sell my data?',
    a: 'No. Revenue is subscriptions and IAP, not ads and not data brokerage. Bank credentials never touch Budgit servers (Plaid).',
  },
];

const Pricing = () => {
  const [processing, setProcessing] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const applePrice = config.PRICING_DISPLAY.APPLE_FOUNDER_MONTHLY;
  const webPrice = config.PRICING_DISPLAY.PREMIUM_FOUNDER_MONTHLY;

  const handleGetStarted = () => {
    navigate('/');
  };

  const handleWebUpgrade = async () => {
    if (!user) {
      navigate('/');
      return;
    }
    try {
      setProcessing(true);
      const { sessionUrl } = await subscriptionService.createCheckoutSession({
        returnUrl: '/pricing',
      });
      window.location.href = sessionUrl;
    } catch (err) {
      console.error('Checkout error:', err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: colors.background, minHeight: '100vh' }}>
      <Helmet>
        <title>Pricing · Budgit</title>
        <meta
          name="description"
          content={`Budgit Premium: $${applePrice}/mo on iOS (Apple IAP) and $${webPrice}/mo on the web (Stripe). Start free with up to ${FREE_BANKS} banks. 90 free days of Premium available as an offer.`}
        />
        <link rel="canonical" href="https://budgit.app/pricing" />
      </Helmet>

      <Box
        component="header"
        sx={{
          px: { xs: 2, md: 4 },
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <RouterLink to="/" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
          <Box
            component="img"
            src="/images/budgit-logo-md.png"
            alt="Budgit"
            sx={{ height: 40 }}
          />
        </RouterLink>
        <Button
          component={RouterLink}
          to="/"
          sx={{ textTransform: 'none', fontWeight: 600, color: colors.secondary }}
        >
          Home
        </Button>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 }, pb: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 800, color: colors.secondary, fontSize: { xs: '2rem', md: '2.75rem' } }}
          >
            Simple pricing. Real AI.
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: colors.secondary, opacity: 0.8, maxWidth: 640, mx: 'auto', mb: 2 }}
          >
            Start free. Experience full AI during onboarding. Keep the personal model, unlimited accounts &amp; budgets, and family sharing with Premium.
          </Typography>
          <Typography variant="body2" sx={{ color: colors.secondary, opacity: 0.7 }}>
            Rated 4.9★ on the App Store (16 ratings).{' '}
            <Link href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" sx={{ color: colors.primary, fontWeight: 700 }}>
              Download on the App Store
            </Link>
          </Typography>
        </Box>

        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} md={6}>
            <PricingCard>
              <CardContent sx={{ p: 4, flexGrow: 1 }}>
                <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
                  Free
                </Typography>
                <Typography variant="body2" sx={{ color: colors.secondary, opacity: 0.7, mb: 2 }}>
                  For getting the habit started without a card.
                </Typography>
                <Box mb={2}>
                  <Typography variant="h3" component="p" fontWeight="bold" sx={{ color: colors.secondary }}>
                    $0
                  </Typography>
                </Box>
                <Divider sx={{ my: 3 }} />
                <List disablePadding>
                  {[
                    `Plaid bank connectivity — up to ${FREE_BANKS} banks`,
                    'AI-powered onboarding (first budget, initial categorization, income detection)',
                    'Manual transaction categorization after onboarding',
                    'Receipt scanner',
                    'Reimbursement tracker',
                    'Manual transaction entry',
                    '1 active budget per budget period',
                    'Community feed + contest participation (limited points)',
                  ].map((name) => (
                    <ListItem key={name} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <FeatureIcon included>
                          <CheckIcon />
                        </FeatureIcon>
                      </ListItemIcon>
                      <ListItemText primary={name} />
                    </ListItem>
                  ))}
                  {[
                    'Ongoing AI auto-categorization',
                    'Low-confidence prompts',
                    'Unlimited accounts & budgets',
                    'Family sharing',
                  ].map((name) => (
                    <ListItem key={name} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <FeatureIcon included={false}>
                          <CloseIcon />
                        </FeatureIcon>
                      </ListItemIcon>
                      <ListItemText
                        primary={name}
                        primaryTypographyProps={{ color: 'text.secondary' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
              <CardActions sx={{ p: 4, pt: 0 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  onClick={handleGetStarted}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '50px', py: 1.25 }}
                >
                  Get started free
                </Button>
              </CardActions>
            </PricingCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <PricingCard featured>
              <Box
                sx={{
                  position: 'absolute',
                  top: -12,
                  right: 20,
                  backgroundColor: colors.primary,
                  color: colors.white,
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                Founding Member
              </Box>
              <CardContent sx={{ p: 4, flexGrow: 1 }}>
                <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
                  Premium
                </Typography>
                <Typography variant="body2" sx={{ color: colors.secondary, opacity: 0.7, mb: 2 }}>
                  For people who want Budgit to keep doing the work, and households that budget together.
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.secondary, mb: 0.5 }}>
                    iOS — Founding Member IAP
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
                    <Typography variant="h3" component="p" fontWeight="bold" sx={{ color: colors.primary }}>
                      ${applePrice}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      /mo
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.secondary, mb: 0.5 }}>
                    Web / other — Stripe
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="h3" component="p" fontWeight="bold" sx={{ color: colors.primary }}>
                      ${webPrice}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      /mo
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="body2" sx={{ color: colors.secondary, opacity: 0.75, mb: 1 }}>
                  90 free days of Premium — cancel anytime in the store or billing portal.
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: colors.secondary, opacity: 0.6, mb: 2 }}>
                  This page describes the offer. You are not enrolled until you opt in or your account is trialing.
                </Typography>

                <Divider sx={{ my: 3 }} />
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: colors.secondary }}>
                  Everything in Free, plus:
                </Typography>
                <List disablePadding>
                  {[
                    'Ongoing AI auto-categorization of new transactions',
                    'Low-confidence prompts when the model wants a quick confirmation',
                    'Unlimited financial accounts',
                    'Unlimited active budgets per period',
                    'Family sharing — spouse collaboration, children allowances, intelligent attribution',
                    'Competitive contest point accumulation',
                  ].map((name) => (
                    <ListItem key={name} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <FeatureIcon included>
                          <CheckIcon />
                        </FeatureIcon>
                      </ListItemIcon>
                      <ListItemText primary={name} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
              <CardActions sx={{ p: 4, pt: 0, flexDirection: 'column', gap: 1.5 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '50px', py: 1.25 }}
                >
                  Unlock Founding Member
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  disabled={processing}
                  onClick={handleWebUpgrade}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '50px', py: 1.25 }}
                >
                  Upgrade to Premium
                </Button>
              </CardActions>
            </PricingCard>
          </Grid>
        </Grid>

        <Box sx={{ mt: 8, p: 3, borderRadius: '16px', bgcolor: `${colors.primary}10` }}>
          <Typography variant="body1" sx={{ color: colors.secondary, fontWeight: 600 }}>
            Every user experiences full AI during onboarding. When onboarding ends, choose: keep AI working for you (Premium) or categorize manually (Free). We demonstrate value before we ask for money.
          </Typography>
        </Box>

        <Box sx={{ mt: 8, overflowX: 'auto' }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 800, color: colors.secondary, mb: 3 }}>
            Compare plans
          </Typography>
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              backgroundColor: colors.white,
              borderRadius: '16px',
              overflow: 'hidden',
              minWidth: 560,
            }}
          >
            <Box component="thead">
              <Box component="tr" sx={{ backgroundColor: `${colors.primary}12` }}>
                {['Feature', 'Free', 'Premium'].map((heading) => (
                  <Box
                    component="th"
                    key={heading}
                    sx={{ textAlign: 'left', p: 2, color: colors.secondary, fontWeight: 800 }}
                  >
                    {heading}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {comparisonRows.map((row) => (
                <Box component="tr" key={row.name} sx={{ borderTop: `1px solid ${colors.primary}15` }}>
                  <Box component="td" sx={{ p: 2, color: colors.secondary, fontWeight: 600 }}>
                    {row.name}
                  </Box>
                  <Box component="td" sx={{ p: 2, color: colors.secondary }}>
                    {row.free}
                  </Box>
                  <Box component="td" sx={{ p: 2, color: colors.secondary }}>
                    {row.premium}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 8 }}>
          <Typography variant="h5" component="h3" sx={{ fontWeight: 800, color: colors.secondary, mb: 1 }}>
            Family accounts — the upgrade couples actually feel
          </Typography>
          <Typography variant="body1" sx={{ color: colors.secondary, opacity: 0.8, maxWidth: 720 }}>
            Budget with a spouse, set kids’ allowances, and let AI help attribute “whose purchase was that?” Family sharing is Premium-only and is a primary reason to upgrade after the solo habit sticks.
          </Typography>
        </Box>

        <Box sx={{ mt: 8 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 800, color: colors.secondary, mb: 3 }}>
            Pricing FAQ
          </Typography>
          {faqs.map((item) => (
            <Box key={item.q} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: colors.secondary, mb: 0.5 }}>
                {item.q}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.secondary, opacity: 0.8, lineHeight: 1.7 }}>
                {item.a}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ textAlign: 'center', mt: 10 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: colors.secondary, mb: 1 }}>
            Start small. Build the habit.
          </Typography>
          <Typography variant="body1" sx={{ color: colors.secondary, opacity: 0.75, mb: 3 }}>
            You don’t need to be good with money — you just need to budgit.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGetStarted}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '50px', px: 4, py: 1.25 }}
            >
              Get started free
            </Button>
            <Button
              variant="outlined"
              color="primary"
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '50px', px: 4, py: 1.25 }}
            >
              App Store 4.9★
            </Button>
          </Box>
          <Typography variant="body2" sx={{ mt: 4, color: colors.secondary, opacity: 0.7, maxWidth: 560, mx: 'auto' }}>
            Premium keeps categorization working for you — Founding on iOS at ${applePrice}/mo, web at ${webPrice}/mo, with a 90 free days Premium offer. Start free anytime.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Pricing;
